export const CHECKOUT_URL = 'https://caregiver-ready.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#comprar'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/emszkbiWzM75V4htwhLKqN/Diseno-sin-titulo-6.webp'

export const insideImages: { src: string; caption: string }[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/rV6tDjqHpbjQbcxR9nYeUG/Captura-de-pantalla-2026-09-10-a-la_s_-7.35.18_p._m..webp',
    caption: 'One Routine. One Organized Day.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/nj4EnjMBdM4fbdmYZTrf6Z/Captura-de-pantalla-2026-09-10-a-la_s_-7.33.39_p._m..webp',
    caption: 'Master Daily Care Planner™',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/ifrVNd5jjKv3Y1hEwRSrZb/Captura-de-pantalla-2026-09-10-a-la_s_-7.32.34_p._m..webp',
    caption: 'Today’s Care Command Center',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/2pMt61JafGC5UCedhk2DN2/Captura-de-pantalla-2026-09-10-a-la_s_-7.34.38_p._m..webp',
    caption: 'Create a Clear Routine Station',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/7obGJkx2Lt4u31zNs5fGVo/Captura-de-pantalla-2026-09-10-a-la_s_-7.35.33_p._m..webp',
    caption: 'Personal Care Routine Dashboard',
  },
]

export const systemChecklist = [
  'Daily care planners',
  'Personal care routines',
  'Meal & hydration trackers',
  'Mobility & safety organizers',
  'Care notes & observation sheets',
  'Shift handoff resources',
  'Family communication tools',
  'Professional resources ready to use',
]

export interface Module {
  number: number
  title: string
  body: string
  steps: string
}

export const modules: Module[] = [
  {
    number: 1,
    title: 'DAILY CARE PLANNER™',
    body: 'Organize the complete care day with ready-to-use daily planners, schedules, priority checklists and routine organizers. Keep morning, afternoon and evening responsibilities clearly structured. Review appointments, reminders and tasks without rebuilding your plan every day. Use the tools alongside the individual’s established care plan.',
    steps: 'Plan. Prioritize. Organize.',
  },
  {
    number: 2,
    title: 'PERSONAL CARE ROUTINES™',
    body: 'Organize everyday personal care with structured routine sheets, checklists and preference tools. Keep morning, evening, grooming, dressing and personal care preparation clearly organized. Use reusable resources instead of recreating the same routine notes every day. Adapt them according to established preferences and care instructions.',
    steps: 'Prepare. Follow. Organize.',
  },
  {
    number: 3,
    title: 'MEALS, HYDRATION & DAILY WELLNESS™',
    body: 'Keep meals, hydration and everyday wellness observations organized in one clear system. Use trackers for meal schedules, hydration, appetite, mood and other factual daily observations. Record relevant information without relying on scattered notes or memory. Use existing dietary and professional instructions as your reference.',
    steps: 'Track. Record. Stay organized.',
  },
  {
    number: 4,
    title: 'MOBILITY & SAFETY SYSTEM™',
    body: 'Organize established mobility routines, everyday safety checks and outing preparation. Keep mobility information, environmental observations and practical reminders together. Use structured checklists alongside the individual’s existing care plan and instructions. Document what matters without rebuilding your system every day.',
    steps: 'Check. Organize. Document.',
  },
  {
    number: 5,
    title: 'CARE NOTES & OBSERVATION SYSTEM™',
    body: 'Keep daily care notes and factual observations structured and easier to complete. Use morning, afternoon and evening notes, observation sheets and routine-change records. Organize appointment information, relevant changes and end-of-day summaries. Record what happened without rebuilding the same notes from scratch.',
    steps: 'Observe. Record. Communicate.',
  },
  {
    number: 6,
    title: 'SHIFT HANDOFF & COMMUNICATION™',
    body: 'Organize the information that needs to move between caregivers, shifts and authorized family members. Use structured handoff sheets, family updates, care summaries and communication records. Keep completed tasks, pending items, observations and important reminders clear. Give the next caregiver an organized overview of the care period.',
    steps: 'Summarize. Handoff. Communicate.',
  },
]

export interface Bonus {
  number: number
  title: string
  subtitle: string
  body: string
  steps: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'CARE RECIPIENT PROFILE & PREFERENCES PACK™',
    subtitle: 'Keep Essential Everyday Information Organized',
    body: 'Organize routines, preferences, contacts and important everyday care information in one place. Use ready-to-complete profiles and reference sheets whenever you need them. Keep established information easier to find and review.',
    steps: 'Profile. Organize. Reference.',
  },
  {
    number: 2,
    title: 'CAREGIVER SHIFT HANDOFF QUICK PACK™',
    subtitle: 'Ready-to-Use Handoff Sheets',
    body: 'Organize completed tasks, pending items, observations and next-shift priorities. Choose from quick, standard and detailed handoff formats. Stop rebuilding the same shift summary from scratch.',
    steps: 'Record. Summarize. Handoff.',
  },
  {
    number: 3,
    title: 'APPOINTMENT & OUTING ORGANIZER PACK™',
    subtitle: 'Practical Appointment & Outing Planning',
    body: 'Organize appointments, transportation, preparation, documents and authorized outings. Use reusable planners, checklists and follow-up trackers. Keep important logistical information together before and after each appointment.',
    steps: 'Prepare. Organize. Follow up.',
  },
  {
    number: 4,
    title: 'CARE SUPPLIES & RESTOCK ORGANIZER™',
    subtitle: 'Everyday Supply Organization',
    body: 'Track everyday care supplies, running-low items and restock needs. Use ready-to-use checklists, supply lists and restock organizers. Keep what is available and what needs attention clearly organized.',
    steps: 'Check. Record. Restock.',
  },
  {
    number: 5,
    title: 'WEEKLY CARE ORGANIZATION RESET™',
    subtitle: 'Prepare the Next Seven Days',
    body: 'Review appointments, schedules, supplies, transportation and pending communication. Organize the practical details of the upcoming seven days. See what is ready, what is pending and what still needs clarification.',
    steps: 'Review. Prepare. Plan.',
  },
]

export const differentiators = [
  {
    emoji: '📋',
    title: 'Ready-to-use professional resources',
    body: 'Open the system and access organized tools instead of rebuilding everything from scratch.',
  },
  {
    emoji: '🗓️',
    title: 'Practical daily organization',
    body: 'Use planners, routines, trackers and checklists to structure everyday care.',
  },
  {
    emoji: '🧩',
    title: '6 essential areas in one system',
    body: 'Keep planning, personal care, meals, mobility, notes and communication organized.',
  },
  {
    emoji: '🔄',
    title: 'Reusable resources',
    body: 'Return to the planners, forms and checklists whenever you need them.',
  },
  {
    emoji: '📂',
    title: 'Everything in one organized place',
    body: 'Stop relying on scattered notes, messages and disconnected documents.',
  },
  {
    emoji: '⏱️',
    title: 'Less repetitive preparation',
    body: 'Spend less time recreating the same organizational resources every day.',
  },
]

export const offerChecklist = [
  '6 professional modules',
  '5 professional bonuses',
  'Daily care planners',
  'Personal care routines',
  'Meal & hydration trackers',
  'Mobility & safety organizers',
  'Care notes & observations',
  'Shift handoff & communication tools',
]

export interface Testimonial {
  name: string
  role: string
  location: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Denise Harrow, 52',
    role: 'Home Care Worker · 14 years',
    location: 'Leeds, United Kingdom',
    quote:
      'I used to arrive at every shift with three different notebooks and a phone full of reminders. Now I print the daily planner and the handoff sheet before I start and the whole day has a shape. My supervisor noticed the difference in my notes within the first week.',
  },
  {
    name: 'Marisol Rivera, 46',
    role: 'Personal Care Aide · Agency staff',
    location: 'Phoenix, United States',
    quote:
      'The meals and hydration tracker is the piece I did not know I needed. I record what was offered, what was accepted and any factual observation, and it is all in the same place at the end of the day. No more trying to remember details four hours later.',
  },
  {
    name: 'Angela Whitcombe, 58',
    role: 'Live-in Caregiver',
    location: 'Hamilton, Canada',
    quote:
      'What sold me was the shift handoff pack. I work opposite another caregiver and we were constantly missing small details between us. The standard handoff sheet takes me four minutes to complete and she starts her shift already knowing everything.',
  },
  {
    name: 'Karen Balfour, 49',
    role: 'Senior Care Assistant',
    location: 'Adelaide, Australia',
    quote:
      'I care for two clients with very different routines. Instead of rewriting checklists, I keep one set of routine sheets per person and simply follow them. The weekly reset on Sundays means I am never scrambling on a Monday morning.',
  },
  {
    name: 'Patricia Oyelaran, 41',
    role: 'Community Caregiver · Domiciliary care',
    location: 'Birmingham, United Kingdom',
    quote:
      'The appointment and outing organizer saved me on transport days. Documents, preparation, timing and follow-up are all on one page. I feel far more professional handing that over to the family than a message typed in a rush.',
  },
]

export interface Faq {
  q: string
  a: string
}

export const faqs: Faq[] = [
  {
    q: 'Is this a physical product?',
    a: 'No. CAREGIVER READY™ is a 100% digital professional resource.',
  },
  {
    q: 'Who is this system for?',
    a: 'Professional caregivers, home care workers and personal care aides looking for organized, ready-to-use resources for everyday care.',
  },
  {
    q: 'Is this caregiver training or a certification?',
    a: 'No. It is an organization and reference system, not a certification or replacement for required professional training.',
  },
  {
    q: 'Is this another ebook filled with theory?',
    a: 'No. The system focuses on practical planners, routines, trackers, checklists, care notes and ready-to-use resources.',
  },
  {
    q: 'Do I have to create the resources myself?',
    a: 'No. The resources are already created and organized. Choose the appropriate tools and use them according to the established care plan and your responsibilities.',
  },
  {
    q: 'What areas are included?',
    a: 'Daily Planning, Personal Care, Meals & Hydration, Mobility & Safety, Care Notes & Observation, and Shift Handoff & Communication.',
  },
  {
    q: 'Can I use the resources in different care situations?',
    a: 'Yes. The organizational structures can be adapted according to the individual care plan, workplace requirements and authorized responsibilities.',
  },
  {
    q: 'Are the resources ready to use?',
    a: 'Yes. They are designed for practical digital reference and everyday organization.',
  },
  {
    q: 'What additional resources are included?',
    a: 'Five bonuses covering care recipient profiles, shift handoffs, appointments & outings, supplies & restocking, and weekly organization.',
  },
  {
    q: 'Does this replace a care plan or medical instructions?',
    a: 'No. CAREGIVER READY™ does not replace individualized care plans, medical instructions, professional training or employer policies.',
  },
  {
    q: 'How will I receive the product?',
    a: 'You’ll receive immediate digital access after your purchase.',
  },
]

export const purchaseNotifications = [
  { name: 'Emma Whitfield', city: 'Manchester', country: 'United Kingdom' },
  { name: 'Sarah Collins', city: 'Toronto', country: 'Canada' },
  { name: 'Michelle Brennan', city: 'Melbourne', country: 'Australia' },
  { name: 'Linda Carter', city: 'Columbus', country: 'United States' },
  { name: 'Joanne Mackay', city: 'Glasgow', country: 'United Kingdom' },
  { name: 'Rachel Dumont', city: 'Ottawa', country: 'Canada' },
  { name: 'Teresa Novak', city: 'San Antonio', country: 'United States' },
  { name: 'Katrina Boyle', city: 'Brisbane', country: 'Australia' },
  { name: 'Deborah Fields', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Nicole Tremblay', city: 'Calgary', country: 'Canada' },
  { name: 'Angela Ruiz', city: 'Tampa', country: 'United States' },
  { name: 'Fiona Pearce', city: 'Perth', country: 'Australia' },
]
